import { useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { collection, query, where, getDocs, limit, doc, getDoc } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { useAuthState } from '@/hooks/useAuth'

/**
 * Public-facing page that handles artist username URLs (e.g., getmoretats.com/artistname)
 * This allows artists to share clean URLs in their bios
 * 
 * Routes based on whether the artist has an active booking system:
 * - If booking system is active: redirects to chat with booking options
 * - If no booking system: redirects to artist profile page (ArtistPage)
 */
export default function ArtistBookingPublicPage() {
  const { artistName } = useParams<{ artistName: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { loading: authLoading } = useAuthState()

  useEffect(() => {
    // Wait for auth to load before proceeding
    if (authLoading) {
      console.log('[ArtistBookingPublicPage] Auth is loading, waiting...')
      return
    }

    // Don't proceed if we don't have an artist name yet
    if (!artistName) {
      console.warn('[ArtistBookingPublicPage] No artist username provided, redirecting to artists')
      navigate('/artists', { replace: true })
      return
    }

    console.log('[ArtistBookingPublicPage] Starting artist lookup for:', artistName)

    // Always proceed with artist lookup (whether user is authenticated or not)
    const findArtistAndRedirect = async () => {
      try {
        // Search for artist by their displayName
        const artistsRef = collection(db, 'artists')
        const artistQuery = query(
          artistsRef,
          where('displayName', '==', artistName),
          limit(1)
        )
        
        console.log('[ArtistBookingPublicPage] Querying artists with displayName:', artistName)
        const querySnapshot = await getDocs(artistQuery)
        console.log('[ArtistBookingPublicPage] Query succeeded, found', querySnapshot.size, 'results')
        
        if (querySnapshot.empty) {
          console.warn('[ArtistBookingPublicPage] Artist not found:', artistName)
          // Redirect to artists page when artist is not found
          navigate('/artists', { replace: true })
          return
        }

        const artistDoc = querySnapshot.docs[0]
        const artistData = artistDoc.data()
        const artistId = artistDoc.id

        console.log('[ArtistBookingPublicPage] Found artist:', { artistId, displayName: artistData.displayName })

        // Check if artist has booking system enabled (always check, regardless of auth status)
        let hasActiveBooking = false
        console.log('[ArtistBookingPublicPage] Checking booking system subscription for artist:', artistId)
        try {
          const bookingSystemRef = doc(db, 'bookingSystems', artistId)
          const bookingSystemDoc = await getDoc(bookingSystemRef)
          console.log('[ArtistBookingPublicPage] Booking system read succeeded')
          
          if (bookingSystemDoc.exists()) {
            const bsData = bookingSystemDoc.data()
            const subscriptionData = bsData?.bookingSubscription
            hasActiveBooking = subscriptionData?.status === 'active' || subscriptionData?.status === 'trial'
            console.log('[ArtistBookingPublicPage] Booking system found:', {
              hasDocument: true,
              subscriptionStatus: subscriptionData?.status,
              hasActiveBooking
            })
          } else {
            console.log('[ArtistBookingPublicPage] No booking system document found for artist')
          }
        } catch (bookingError) {
          console.warn('[ArtistBookingPublicPage] Could not read booking system:', bookingError)
        }

        // Route based on booking system status
        if (hasActiveBooking) {
          // Artist has active booking system - redirect to chat
          console.log('[ArtistBookingPublicPage] Artist has active booking system, redirecting to chat for artistId:', artistId)
          navigate(`/chats/new?artistId=${artistId}&source=bio_link&t=${Date.now()}`, { replace: true })
        } else {
          // Artist does not have booking system - redirect to artist profile page
          console.log('[ArtistBookingPublicPage] Artist does not have active booking system, redirecting to artist profile:', artistId)
          navigate(`/artist/${artistId}`, { replace: true })
        }
        
      } catch (error) {
        console.error('[ArtistBookingPublicPage] Error finding artist:', error)
        // On any error, redirect to artists page as fallback
        navigate('/artists', { replace: true })
      }
    }

    void findArtistAndRedirect()
  }, [artistName, navigate, authLoading, location.pathname])

  // Show loading state while looking up artist and redirecting to chat
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground mx-auto mb-4"></div>
        <p className="text-foreground">Loading...</p>
      </div>
    </div>
  )
}

